import { ImageFromCdn, Row, Typography } from '@/components'
import { Categories, CategoryId, ThemeColor } from '@/enums'
import { paths } from '@/routes/paths'
import { theme } from '@/theme'
import { formatMoney, useGoToPath } from '@/utils'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  ContentCardButtonWrapper,
  ContentCardHeader,
  ViewAllCardWrapper,
} from '../styles'
import { CategoryCardConfig } from './MissingCoverageSection'
import { CategoryCardConfigItem } from '../helpers'

const ContentCardImage = styled(ImageFromCdn)<
  Omit<CategoryCardConfig['image'], 'imageUrl'>
>`
  position: absolute;
  bottom: ${({ bottom }) => bottom || '-24px'};
  right: ${({ right }) => right || '-32px'};
  width: ${({ width }) => width || 'auto'};
  height: ${({ height }) => height || '100%'};
`

interface ContentCardProps {
  locationId: string
  categoryId: CategoryId
  categoryCardConfig: CategoryCardConfigItem
  paymentPeriod: string
}

export const ContentCard = ({
  locationId,
  categoryId,
  categoryCardConfig,
  paymentPeriod,
}: ContentCardProps) => {
  const { t } = useTranslation()
  const categoryTranslation = t(`categories.${Categories[categoryId].key}.name`)
  const paymentPeriodTranslation = t(`paymentPeriodNouns.${paymentPeriod}`)
  const goToInsuranceFinderCategory = useGoToPath(
    paths.viewInsuranceWithKey({
      locationId,
      categoryId,
    }),
  )

  const { imageUrl, ...rest } = categoryCardConfig.image

  return (
    <ContentCardButtonWrapper
      onClick={() => goToInsuranceFinderCategory()}
      role="button"
    >
      <ContentCardHeader>
        <Row gap="16px" justifyContent="space-between">
          <Typography ellipsis bold variant="p2Body">
            {categoryTranslation}
          </Typography>
          <ChevronRight size={16} />
        </Row>
        <Typography ellipsis variant="p2Body" color={ThemeColor.b50}>
          {categoryCardConfig.samplePrice
            ? t('locationPage.fromPrice', {
                price: formatMoney(categoryCardConfig.samplePrice),
                period: paymentPeriodTranslation,
              })
            : t('locationPage.noSamplePrice')}
        </Typography>
      </ContentCardHeader>
      <ContentCardImage
        name={imageUrl}
        alt="Content Insurance"
        type="webp"
        fallbackType="png"
        {...rest}
      />
    </ContentCardButtonWrapper>
  )
}

interface ViewAllCardProps {
  locationId: string
}

export const ViewAllCard = ({ locationId }: ViewAllCardProps) => {
  const { t } = useTranslation()
  const goToFindInsurances = useGoToPath(
    paths.findInsurancesWithKey(locationId),
  )

  return (
    <ViewAllCardWrapper role="button" onClick={() => goToFindInsurances()}>
      <ContentCardHeader>
        <Row gap="8px" alignItems="center" justifyContent="center">
          <Typography bold variant="p2Body" color={ThemeColor.b0}>
            {t('locationPage.viewAllCoverage')}
          </Typography>
          <ChevronRight size={16} color={theme.color.b0} />
        </Row>
      </ContentCardHeader>
    </ViewAllCardWrapper>
  )
}
